// ---------------------------------------------------------------------------
// Manager domain types
// Ported from trusstor_dashboard/shared/interfaces/
// ---------------------------------------------------------------------------

import { z } from 'zod';
import { dateField } from './_shared.js';
import { tradeSchema } from './profession.js';
import { IBaseStaticTag, IBaseMergeTag } from './tag.js';
import type { IFloor } from './floor.js';

// ---------------------------------------------------------------------------
// phoneNumberField (inline — trusstor @shared/schemas helper)
// ---------------------------------------------------------------------------

// NULLABLE, because upstream sends null for a manager with no number on file —
// observed on the simulation project, where it invalidated every safety event
// in the response (the whole array was parsed as one unit, so one null emptied
// the feed and the assistant reported "no safety events on site").
const phoneNumberField = z.string().nullish();

// ---------------------------------------------------------------------------
// managerSchema / IManager
// (from trusstor_dashboard/shared/interfaces/IManager.ts)
// Full schema: baseManagerSchema + dateCreated + dateDeleted
// ---------------------------------------------------------------------------

export const baseManagerSchema = tradeSchema
	.extend({
		projectId: z.string(),
		organizationId: z.string(),
		name: z.string(),
		phoneNumber: phoneNumberField,
		receiveNotifications: z.boolean().optional(),
	})
	.passthrough();

export const managerSchema = baseManagerSchema
	.extend({
		dateCreated: dateField,
		dateDeleted: dateField.optional(),
	})
	.passthrough();

export type IManager = z.infer<typeof managerSchema>;

// ---------------------------------------------------------------------------
// IManagerStaticTag
// (from trusstor_dashboard/shared/interfaces/IManagerStaticTag.ts)
// ---------------------------------------------------------------------------

export interface IManagerStaticTag extends IBaseStaticTag {
	manager: IManager;
}

// ---------------------------------------------------------------------------
// IManagerMergedTag
// (from trusstor_dashboard/shared/interfaces/IManagerMergedTag.ts)
// ---------------------------------------------------------------------------

export interface IManagerMergedTag extends IManagerStaticTag, IBaseMergeTag {}

// ---------------------------------------------------------------------------
// IActiveManager
// (from trusstor_dashboard/shared/interfaces/IActiveManager.ts)
// ---------------------------------------------------------------------------

export interface IActiveManager extends IManager {
	disconnected?: boolean;
	currentFloor?: Omit<IFloor, 'projectId'>;
}
